/**
 * The media plane, from the browser's side. Microphone, peer connection, SDP.
 *
 * This file is short, and that is the entire point of the architecture: the
 * browser talks DIRECTLY to OpenAI over SRTP, so there is no relay to manage, no
 * reconnect dance, no jitter buffer of ours, no playout scheduler, and no echo
 * canceller we wrote. Our server appears exactly once, as an HTTP proxy for the
 * SDP exchange, and never again in the media path.
 *
 * FOUR DECISIONS ENCODED HERE, each of which costs a day if reversed:
 *
 * 1. NO DATA CHANNEL. Not `oai-events`, not any other. With one, the provider's
 *    `session.created` hands the end user the complete system prompt and every
 *    tool schema verbatim, and the user can `session.update` from devtools to
 *    overwrite our instructions. It also creates a duplicate-delivery race where
 *    the browser and the sideband both see the same function call and a
 *    side-effecting tool fires twice. The channel must be created BEFORE
 *    `createOffer()`, so this is not a thing that can be added later in the flow
 *    by accident — it is a thing you would have to come here and add.
 *
 * 2. THE OFFER IS NON-TRICKLE. We POST one complete SDP and get one answer back,
 *    so ICE gathering has to finish first. There is a timeout on that wait,
 *    because `icegatheringstate` occasionally never reaches `complete` on a
 *    machine with an exotic interface list, and a demo that hangs forever at
 *    "connecting" is worse than one that connects on host candidates only.
 *
 * 3. NO `iceServers`. The provider's endpoint is a public host and the offer
 *    goes over HTTPS, so host candidates plus the server's own candidate are
 *    enough — this is what OpenAI's own reference clients do. Adding a public
 *    STUN server would add a third-party dependency to every call for a
 *    benefit we cannot demonstrate.
 *
 * 4. ECHO CANCELLATION STAYS ON. Without it the model hears its own voice from
 *    your speakers, treats it as user speech, and interrupts itself — which
 *    looks exactly like a barge-in bug in the provider. All three of the
 *    processing constraints are `{ ideal: true }` and never `exact`: `exact`
 *    throws OverconstrainedError on devices whose driver does the processing
 *    itself and refuses to admit it, and then there is no call at all.
 */
export class CallHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: string,
  ) {
    super(message)
    this.name = 'CallHttpError'
  }
}

/*
 * MINTING THE SESSION IS NOT HERE. `POST /api/sessions` returns
 * `CreateSessionResponse`, which is part of the frozen wire contract, and api.ts
 * already has one typed caller for it — `sessions.create`. Two hand-rolled
 * fetches that disagree about a field name is exactly the bug that costs an
 * afternoon. The SDP exchange below stays local because it is not JSON: it posts
 * `application/sdp` and gets SDP back, which the JSON helper cannot express.
 */

/**
 * The microphone.
 *
 * `channelCount: { ideal: 1 }` matters more than it looks: a stereo mic that we
 * accept as stereo doubles the Opus payload for no benefit, and the provider
 * downmixes anyway.
 */
export async function openMicrophone(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      autoGainControl: { ideal: true },
      channelCount: { ideal: 1 },
    },
    video: false,
  })
}

export interface PeerHandles {
  pc: RTCPeerConnection
  sender: RTCRtpSender
}

/**
 * Build the peer connection and wire the remote audio to an element.
 *
 * The `<audio>` element is passed in rather than created here because it has to
 * be in the document and it has to be reachable from the user gesture that
 * started the call — autoplay policy. A detached element that is never appended
 * plays nothing, silently, and looks precisely like the provider sending no
 * audio. The Web Audio fallback recorder ALSO depends on this element having its
 * `srcObject` set: without it Chrome's `MediaStreamAudioSourceNode` for a remote
 * WebRTC track yields silence.
 *
 * `onRemote` IS CALLED SYNCHRONOUSLY INSIDE `ontrack`, and it is a callback
 * rather than a promise for exactly that reason: the receiver transform has to
 * be installed before the first packet is handed to the decoder, and a promise
 * resolution is a microtask later. One microtask is unlikely to cost a packet,
 * but "unlikely" here means "the first word of the greeting is missing from the
 * recording on some machines", which is not a bug anybody would find quickly.
 */
export function createPeer(
  mic: MediaStream,
  audioEl: HTMLAudioElement,
  onRemote: (receiver: RTCRtpReceiver, stream: MediaStream) => void,
): PeerHandles {
  const pc = new RTCPeerConnection()

  pc.ontrack = (ev) => {
    const stream = ev.streams[0] ?? new MediaStream([ev.track])
    audioEl.srcObject = stream
    try {
      onRemote(ev.receiver, stream)
    } catch {
      // Recording setup must never break playback, and this handler is the
      // playback path.
    }
    // `autoplay` on the element is not always enough when the element was
    // rendered before the gesture, so play() is called too. A rejection here is
    // not fatal — it means the browser wants another gesture — so it is
    // swallowed rather than thrown into the ontrack handler.
    void audioEl.play().catch(() => {})
  }

  const track = mic.getAudioTracks()[0]
  if (!track) throw new Error('the microphone stream carries no audio track')
  const sender = pc.addTrack(track, mic)

  return { pc, sender }
}

/**
 * Wait for ICE gathering, then hand back the complete offer SDP.
 *
 * `setLocalDescription()` with no argument does createOffer + set in one step and
 * is the modern spelling; it also avoids the classic bug where the offer that
 * gets POSTed is not the one that was set.
 */
export async function makeOffer(pc: RTCPeerConnection, gatherTimeoutMs = 3000): Promise<string> {
  await pc.setLocalDescription()
  if (pc.iceGatheringState !== 'complete') {
    await new Promise<void>((resolve) => {
      const timer = setTimeout(finish, gatherTimeoutMs)
      function finish() {
        clearTimeout(timer)
        pc.removeEventListener('icegatheringstatechange', onChange)
        resolve()
      }
      function onChange() {
        if (pc.iceGatheringState === 'complete') finish()
      }
      pc.addEventListener('icegatheringstatechange', onChange)
    })
  }
  const sdp = pc.localDescription?.sdp
  if (!sdp) throw new Error('no local description after ICE gathering')
  return sdp
}

/**
 * POST the offer to our edge, which forwards it to the provider with our real
 * API key and reads the `call_id` off the `Location` header server-side. The
 * browser is never told the call id and never holds a provider credential.
 */
export async function exchangeSdp(sessionId: string, offerSdp: string, signal?: AbortSignal): Promise<string> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(sessionId)}/sdp`, {
    method: 'POST',
    headers: { 'content-type': 'application/sdp' },
    body: offerSdp,
    signal: signal ?? null,
  })
  const text = await res.text()
  if (!res.ok) {
    throw new CallHttpError(`the provider refused the offer (HTTP ${res.status})`, res.status, text)
  }
  if (!text.startsWith('v=')) {
    throw new CallHttpError('the SDP answer does not look like SDP', res.status, text.slice(0, 200))
  }
  return text
}

/** Mute without renegotiating. `enabled = false` keeps the RTP flowing as
 *  silence, so the provider does not see the track disappear and the recorder
 *  keeps a continuous timeline through the muted stretch. */
export function teardown(pc: RTCPeerConnection, mic: MediaStream | null): void {
  try {
    for (const s of pc.getSenders()) s.track?.stop()
  } catch {
    /* the connection may already be closed; stopping tracks is best-effort */
  }
  if (mic) for (const t of mic.getTracks()) t.stop()
  try {
    pc.close()
  } catch {
    /* idempotent by intent */
  }
}
